import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { toast } from "react-toastify";
import Joi from "joi-browser";
import _ from "lodash";
import moment from "moment";
import Input from "./Input";
import Select from "./Select";
import AllergiesTable from "./AllergiesTable";
import Pagination from "./Pagination";
import allergyOptionSets from "../../options/allergy.json";
import { proxyCall } from "../services/soar";
import {
  paginate,
  updateEntity,
  createEntity,
  getFieldName,
} from "./../../scripts/helperScripts";

function AllergyModal({
  isOpen,
  hideModal,
  nextModal,
  isLastModal,
  clientId,
  clientProgram,
  initialData,
  updateAllergies,
}) {
  const [selectedAllergy, setSelectedAllergy] = useState({});
  const [allergySearchQuery, setAllergySearchQuery] = useState("");
  const [searchType, setSearchType] = useState("startsWith");
  const [allergyResults, setAllergyResults] = useState([]);
  const [totalCount, setTotalCount] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState({
    path: "Description",
    order: "asc",
  });
  const [allergyTypeOptions, setAllergyTypeOptions] = useState([]);
  const [allergyReactionOptions, setAllergyReactionOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const isMountedRef = useRef(true);
  const navigate = useNavigate();
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  // const convert = require("xml-js");
  const pageSize = 5;
  const FDBMedVersion = "FDBMed_2_9";

  const joiSchema = {
    ccx_client_allergyid: Joi.string().allow(null, ""),
    UID: Joi.string().required(),
    IdType: Joi.string().required(),
    Description: Joi.string().required(),
    allergyType: Joi.string().required().label("Allergy Type"),
    allergyReaction: Joi.string().required().label("Allergy Reaction"),
    ccx_severity: Joi.string().required().label("Allergy Severity"),
    ccx_effdate: Joi.alternatives([
      Joi.date()
        .required()
        .max("now")
        .min(new Date("1/1/1900"))
        .label("Effective Date")
        .error((errors) => {
          errors.forEach((error) => {
            switch (error.type) {
              case "date.max":
                error.message = `Effective Date cannot be future dated`;
                break;
              case "date.min":
                error.message = `Effective Date cannot be before 1/1/1900`;
                break;
              default:
                return (error.message = "Invalid Effective Date");
            }
          });
          return errors;
        }),
    ]),
    ccx_expdate: Joi.alternatives([
      Joi.date()
        .max("now")
        .min(new Date(selectedAllergy.ccx_effdate))
        .allow(null)
        .label("Expiration Date")
        .error((errors) => {
          errors.forEach((error) => {
            switch (error.type) {
              case "date.min":
                error.message = `Expiration Date needs to be after Effective Date of ${error.context.limit}`;
                break;
              case "date.max":
                error.message = `Expiration Date cannot be future dated`;
                break;
              default:
                return (error.message = "Invalid Effective Date");
            }
          });
          return errors;
        }),
    ]),
  };

  useEffect(() => {
    if (!safyInfo) navigate("/");
  });

  useEffect(() => {
    return () => (isMountedRef.current = false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadAllergyTypes();
      loadAllergyReactions();
    }
  }, [isOpen]);

  useEffect(() => {
    if (initialData) setSelectedAllergy(initialData);
  }, [initialData]);

  const loadAllergyTypes = () => {
    proxyCall("https://dataservices.cocentrix.com/api/Allergy/AllergyTypes")
      .then((response) => {
        if (response && isMountedRef.current) {
          const result = JSON.parse(response);
          const options = result.map((allergyTypeDto) => ({
            _id: allergyTypeDto.Code,
            name: allergyTypeDto.Description,
            codingStandard: allergyTypeDto.CodingStandard,
          }));
          options.push({
            _id: "Not Applicable",
            name: "Not Applicable",
            codingStandard: "Not Applicable",
          });
          setAllergyTypeOptions(options);
        }
      })
      .catch(() => {
        toast.error("An error occurred loading Allergy types.");

        // #region Dummy Data
        //   toast.warning("Live data not retrieved, populating with dummy data");
        //   const xml =
        //     "<ArrayOfAllergyTypeDto xmlns:i='http://www.w3.org/2001/XMLSchema-instance' xmlns='http://schemas.datacontract.org/2004/07/Ccx.Xrm.DataServices.Api'><AllergyTypeDto><Code>235719002</Code><CodingStandard>SNOMED</CodingStandard><Description>Food Intolerance</Description></AllergyTypeDto><AllergyTypeDto><Code>414285001</Code><CodingStandard>SNOMED</CodingStandard><Description>Food Allergy</Description></AllergyTypeDto><AllergyTypeDto><Code>416098002</Code><CodingStandard>SNOMED</CodingStandard><Description>Drug Allergy</Description></AllergyTypeDto><AllergyTypeDto><Code>419199007</Code><CodingStandard>SNOMED</CodingStandard><Description>Allergy to Substance</Description></AllergyTypeDto><AllergyTypeDto><Code>59037007</Code><CodingStandard>SNOMED</CodingStandard><Description>Drug Intolerance</Description></AllergyTypeDto></ArrayOfAllergyTypeDto>";
        //   const result = convert.xml2js(xml, { compact: true });
        //   const options = result.ArrayOfAllergyTypeDto.AllergyTypeDto.map(
        //     (allergyTypeDto) => ({
        //       _id: allergyTypeDto.Code._text,
        //       name: allergyTypeDto.Description._text,
        //       codingStandard: allergyTypeDto.CodingStandard._text,
        //     })
        //   );
        //   options.push({
        //     _id: "Not Applicable",
        //     name: "Not Applicable",
        //     codingStandard: "Not Applicable",
        //   });
        //   setAllergyTypeOptions(options);
        // #endregion
      });
  };

  const loadAllergyReactions = () => {
    proxyCall("https://dataservices.cocentrix.com/api/Allergy/AllergyReactions")
      .then((response) => {
        if (response && isMountedRef.current) {
          const result = JSON.parse(response);
          const options = result.map((allergyTypeDto) => ({
            _id: allergyTypeDto.Code,
            name: allergyTypeDto.Description,
            codingStandard: allergyTypeDto.CodingStandard,
          }));
          options.push({
            _id: "Not Applicable",
            name: "Not Applicable",
            codingStandard: "Not Applicable",
          });
          setAllergyReactionOptions(options);
        }
      })
      .catch(() => {
        toast.error("An error occurred loading Allergy reactions.");

        // Dummy Data
        // toast.warning("Live data not retrieved, populating with dummy data");const xml ="<ArrayOfAllergyTypeDto xmlns:i='http://www.w3.org/2001/XMLSchema-instance' xmlns='http://schemas.datacontract.org/2004/07/Ccx.Xrm.DataServices.Api'><AllergyTypeDto><Code>162397003</Code><CodingStandard>SNOMED</CodingStandard><Description>Sore throat</Description></AllergyTypeDto><AllergyTypeDto><Code>247472004</Code><CodingStandard>SNOMED</CodingStandard><Description>Hives</Description></AllergyTypeDto><AllergyTypeDto><Code>25064002</Code><CodingStandard>SNOMED</CodingStandard><Description>Headache</Description></AllergyTypeDto><AllergyTypeDto><Code>261665006</Code><CodingStandard>SNOMED</CodingStandard><Description>Unknown</Description></AllergyTypeDto><AllergyTypeDto><Code>267036007</Code><CodingStandard>SNOMED</CodingStandard><Description>Dyspnea (shortness of breath)</Description></AllergyTypeDto><AllergyTypeDto><Code>271795006</Code><CodingStandard>SNOMED</CodingStandard><Description>Malaise/fatigue</Description></AllergyTypeDto><AllergyTypeDto><Code>271807003</Code><CodingStandard>SNOMED</CodingStandard><Description>Rash</Description></AllergyTypeDto><AllergyTypeDto><Code>386661006</Code><CodingStandard>SNOMED</CodingStandard><Description>Fever</Description></AllergyTypeDto><AllergyTypeDto><Code>39579001</Code><CodingStandard>SNOMED</CodingStandard><Description>Anaphylaxis</Description></AllergyTypeDto><AllergyTypeDto><Code>404640003</Code><CodingStandard>SNOMED</CodingStandard><Description>Dizziness</Description></AllergyTypeDto><AllergyTypeDto><Code>41291007</Code><CodingStandard>SNOMED</CodingStandard><Description>Angioedema</Description></AllergyTypeDto><AllergyTypeDto><Code>418290006</Code><CodingStandard>SNOMED</CodingStandard><Description>Itching</Description></AllergyTypeDto><AllergyTypeDto><Code>419284004</Code><CodingStandard>SNOMED</CodingStandard><Description>Altered mental status</Description></AllergyTypeDto><AllergyTypeDto><Code>422400008</Code><CodingStandard>SNOMED</CodingStandard><Description>Vomiting</Description></AllergyTypeDto><AllergyTypeDto><Code>422587007</Code><CodingStandard>SNOMED</CodingStandard><Description>Nausea</Description></AllergyTypeDto><AllergyTypeDto><Code>43724002</Code><CodingStandard>SNOMED</CodingStandard><Description>Chills</Description></AllergyTypeDto><AllergyTypeDto><Code>4386001</Code><CodingStandard>SNOMED</CodingStandard><Description>Bronchospasm</Description></AllergyTypeDto><AllergyTypeDto><Code>49727002</Code><CodingStandard>SNOMED</CodingStandard><Description>Cough</Description></AllergyTypeDto><AllergyTypeDto><Code>56018004</Code><CodingStandard>SNOMED</CodingStandard><Description>Wheezing</Description></AllergyTypeDto><AllergyTypeDto><Code>57676002</Code><CodingStandard>SNOMED</CodingStandard><Description>Arthralgia</Description></AllergyTypeDto><AllergyTypeDto><Code>62315008</Code><CodingStandard>SNOMED</CodingStandard><Description>Diarrhea</Description></AllergyTypeDto><AllergyTypeDto><Code>64531003</Code><CodingStandard>SNOMED</CodingStandard><Description>Rhinorrhea</Description></AllergyTypeDto><AllergyTypeDto><Code>65124004</Code><CodingStandard>SNOMED</CodingStandard><Description>Swelling</Description></AllergyTypeDto><AllergyTypeDto><Code>68235000</Code><CodingStandard>SNOMED</CodingStandard><Description>Nasal congestion</Description></AllergyTypeDto><AllergyTypeDto><Code>68962001</Code><CodingStandard>SNOMED</CodingStandard><Description>Myalgia (muscle pain)</Description></AllergyTypeDto><AllergyTypeDto><Code>76067001</Code><CodingStandard>SNOMED</CodingStandard><Description>Sneezing</Description></AllergyTypeDto><AllergyTypeDto><Code>80313002</Code><CodingStandard>SNOMED</CodingStandard><Description>Palpitations</Description></AllergyTypeDto><AllergyTypeDto><Code>95388000</Code><CodingStandard>SNOMED</CodingStandard><Description>Pain/soreness at injection site</Description></AllergyTypeDto><AllergyTypeDto><Code>95516005</Code><CodingStandard>SNOMED</CodingStandard><Description>Gastrointestinal irritation</Description></AllergyTypeDto></ArrayOfAllergyTypeDto>";const result = convert.xml2js(xml, { compact: true });const options = result.ArrayOfAllergyTypeDto.AllergyTypeDto.map((allergyTypeDto) => ({_id: allergyTypeDto.Code._text,name: allergyTypeDto.Description._text,codingStandard: allergyTypeDto.CodingStandard._text,}));options.push({_id: "Not Applicable",name: "Not Applicable",codingStandard: "Not Applicable",});setAllergyReactionOptions(options);
      });
  };

  const handleAllergySearch = () => {
    proxyCall(
      `https://fdb.cocentrix.com/${FDBMedVersion}/api/FirstDatabankMedKnowledge/Allergy?${searchType}=${allergySearchQuery}`
    )
      .then((response) => {
        if (response && isMountedRef.current) {
          console.log("AllergySearchResponse: ", JSON.parse(response));

          const allergies = JSON.parse(response).Allergies.map((allergy) => ({
            UID: allergy.UID,
            IdType: allergy.IdType,
            Description: allergy.Description,
          }));

          setAllergyResults(allergies);
          console.log("SetAllergyResults: ", allergies);
        }
      })
      .catch((ex) => {
        toast.error("An error occurred while searching Allergies.");

        // Dummy Data
        // toast.warning("Live data not retrieved, populating with dummy data");const response = {Traces: [],Allergies: [{Traces: [],UID: "FDB.DRUGNAME+26385",IdType: "DrugName",Description: "Appearex",RxNormIds: ["1588", "705766"],RxNormId: "705766",RxNormDescription: "Appearex",DtoVersion: "1.2",},{Traces: [],UID: "FDB.INGREDIENT+10012",IdType: "Ingredient",Description: "pear",RxNormIds: ["1367125"],RxNormId: "1367125",RxNormDescription: "pear preparation",DtoVersion: "1.2",},{Traces: [],UID: "FDB.INGREDIENT+15124",IdType: "Ingredient",Description: "peas",RxNormIds: ["1426912"],RxNormId: "1426912",RxNormDescription: "Pisum sativum (pea) extract",DtoVersion: "1.2",},{Traces: [],UID: "FDB.INGREDIENT+17614",IdType: "Ingredient",Description: "spearmint",RxNormIds: ["901313"],RxNormId: "901313",RxNormDescription: "spearmint allergenic extract",DtoVersion: "1.2",},{Traces: [],UID: "FDB.INGREDIENT+2473",IdType: "Ingredient",Description: "peanut oil",RxNormIds: ["18235"],RxNormId: "18235",RxNormDescription: "peanut oil",DtoVersion: "1.2",},{Traces: [],UID: "FDB.INGREDIENT+568",IdType: "Ingredient",Description: "peanut",RxNormIds: ["891658"],RxNormId: "891658",RxNormDescription: "peanut allergenic extract",DtoVersion: "1.2",},{Traces: [],UID: "FDB.INGREDIENT+6396",IdType: "Ingredient",Description: "European mistletoe",RxNormIds: ["350489"],RxNormId: "350489",RxNormDescription: "Viscum album preparation",DtoVersion: "1.2",},{Traces: [],UID: "FDB.INGREDIENT+7909",IdType: "Ingredient",Description: "peach",RxNormIds: [],RxNormId: null,RxNormDescription: null,DtoVersion: "1.2",},],NotProcessed: [],GcnSeqNoNotProcessed: [],MedIdNotProcessed: [],NdcNotProcessed: [],OrdMedIdNotProcessed: [],DtoVersion: "1.2",};const allergies = response.Allergies.map((allergy) => ({UID: allergy.UID,IdType: allergy.IdType,Description: allergy.Description,}));setAllergyResults(allergies);
      });
  };

  const handleChange = ({ currentTarget: input }) => {
    const errs = { ...errors };
    const errorMessage = validateProperty(input);
    if (errorMessage) {
      errs[input.name] = errorMessage;
      console.log("Errors:", JSON.stringify(errs));
    } else {
      delete errs[input.name];
    }
    const data = { ...selectedAllergy };
    data[input.name] = input.value;
    setSelectedAllergy(data);

    if (input.type === "date" && input.value === "") {
    }

    setErrors(errs);
    console.log("SetAllergy: ", selectedAllergy);
  };

  const handleAllergySelect = (allergy) => {
    allergy.ccx_effdate = moment().format("YYYY-MM-DD");
    setSelectedAllergy(allergy);
    const errs = {};
    setErrors(errs);
  };

  const validateProperty = ({ name, value }) => {
    const obj = { [name]: value };
    const schema = { [name]: joiSchema[name] };
    const { error } = Joi.validate(obj, schema);
    return error ? error.details[0].message : null;
  };

  const validate = () => {
    const options = { abortEarly: false };
    const { error } = Joi.validate(selectedAllergy, joiSchema, options);
    if (!error) return null;

    let validationErrors = {};
    for (let item of error.details)
      validationErrors[item.path[0]] = item.message;
    setErrors(validationErrors);
    return validationErrors;
  };

  const handleSave = () => {
    const allergyType = allergyTypeOptions.filter(
      (type) => type._id === selectedAllergy.allergyType
    )[0];
    const allergyReaction = allergyReactionOptions.filter(
      (reaction) => reaction._id === selectedAllergy.allergyReaction
    )[0];

    const obj = {
      ccx_patient: {
        type: "entity",
        value: {
          entityName: "contact",
          entityId: clientId,
        },
      },
      ccx_name: {
        type: "string",
        value: selectedAllergy.Description,
      },
      ccx_allergystr: {
        type: "string",
        value: selectedAllergy.Description,
      },
      ccx_allergycodetype: {
        type: "string",
        value: selectedAllergy.IdType,
      },
      ccx_allergycode: {
        type: "string",
        value: selectedAllergy.UID.replace("+", "%2B"),
      },
      ccx_allergytype: {
        type: "string",
        value: allergyType.name,
      },
      ccx_allergytypecodetype: {
        type: "string",
        value: allergyType.codingStandard,
      },
      ccx_allergytypecode: {
        type: "string",
        value: allergyType._id,
      },
      ccx_reactionstr: {
        type: "string",
        value: allergyReaction.name,
      },
      ccx_reactioncodetype: {
        type: "string",
        value: allergyReaction.codingStandard,
      },
      ccx_reactioncode: {
        type: "string",
        value: allergyReaction._id,
      },
      ccx_severity: {
        type: "optionset",
        value: selectedAllergy.ccx_severity
          ? parseInt(selectedAllergy.ccx_severity)
          : null,
      },
      ccx_effdate: {
        type: "datetime",
        value: new Date(selectedAllergy.ccx_effdate),
      },
      ccx_expdate: {
        type: "datetime",
        value: selectedAllergy.ccx_expdate
          ? new Date(selectedAllergy.ccx_expdate)
          : null,
      },
    };

    if (selectedAllergy.ccx_client_allergyid !== undefined) {
      const toastId = toast.loading("Allergy is being updated...", {
        type: toast.TYPE.INFO,
      });
      updateEntity(
        "ccx_client_allergy",
        selectedAllergy.ccx_client_allergyid,
        JSON.stringify(obj),
        safyInfo
      )
        .then(() => {
          toast.update(toastId, {
            render: "Allergy has been created.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
          updateAllergies && updateAllergies(true);
        })
        .catch(() => {
          toast.update(toastId, {
            render: "An error occurred while creating the Allergy.",
            type: toast.TYPE.ERROR,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
        });
    } else {
      const toastId = toast.loading("Allergy is being created...", {
        type: toast.TYPE.INFO,
      });
      createEntity(
        "ccx_client_allergy",
        JSON.stringify(obj),
        safyInfo,
        getFieldName(clientProgram.ccx_company).split(" of ")[1]
      )
        .then(() => {
          toast.update(toastId, {
            render: "Allergy has been updated.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
          updateAllergies && updateAllergies(true);
        })
        .catch(() => {
          toast.update(toastId, {
            render: "An error occurred while updating the Allergy.",
            type: toast.TYPE.ERROR,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
        });
    }
  };

  const handleSaveAndAdd = () => {
    handleSave();
    resetForm();
  };

  const resetForm = () => {
    setSelectedAllergy({});
    setAllergyResults([]);
    setTotalCount(0);
    setCurrentPage(1);
    setErrors({});
  };

  const handleHideModal = () => {
    if (hideModal) {
      resetForm();
      hideModal();
    }
  };

  const getPagedData = () => {
    const sorted = _.orderBy(
      allergyResults,
      [sortColumn.path],
      [sortColumn.order]
    );

    const allergies = paginate(sorted, currentPage, pageSize);
    return allergies;
  };

  useEffect(() => {
    setTotalCount(allergyResults.length);
  }, [allergyResults]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSort = (sortColumn) => {
    setSortColumn(sortColumn);
  };

  const searchTypeOptions = [
    {
      _id: "startsWith",
      name: "Starts with",
    },
    {
      _id: "contains",
      name: "Contains",
    },
    {
      _id: "soundsLike",
      name: "Sounds like",
    },
  ];

  const handleNoAllergiesClick = () => {
    setSelectedAllergy({
      UID: "FDB.ALGNGRP+900388",
      IdType: "AllergenGroup",
      Description: "No Known Allergies",
      allergyType: "Not Applicable",
      allergyReaction: "Not Applicable",
      ccx_severity: "803080003",
      ccx_effdate: moment().format("YYYY-MM-DD"),
    });
  };

  return isOpen ? (
    <Modal show={isOpen} animation={false}>
      <Modal.Header>
        <div className="clearfix w-100">
          <div className="float-start">
            <Modal.Title>Allergy</Modal.Title>
          </div>
          <div className="float-end pt-2">
            <button className="btn btn-link" onClick={handleNoAllergiesClick}>
              No Known Allergies
            </button>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        {!selectedAllergy.UID && (
          <>
            <Select
              name="searchType"
              label="Search Type"
              options={searchTypeOptions}
              defaultValue={searchType}
              onChange={({ currentTarget: input }) => {
                setSearchType(input.value);
              }}
            />
            {searchType === "" && (
              <div className="alert alert-danger">
                'Search Type' is required.
              </div>
            )}
            <div className="container">
              <div className="row">
                <div className="col">
                  <Input
                    name="allergySearch"
                    label="Search for Allergy"
                    onChange={(e) => {
                      setAllergySearchQuery(e.currentTarget.value);
                    }}
                    placeholder="Search for Allergy"
                  />
                </div>
                <div className="col-3">
                  <div></div>
                  <Button
                    style={{ marginTop: "30px" }}
                    className={
                      searchType === ""
                        ? "btn btn-secondary"
                        : "btn btn-primary"
                    }
                    onClick={() => handleAllergySearch()}
                    disabled={searchType === ""}
                  >
                    Search
                  </Button>
                </div>
              </div>
            </div>
            <AllergiesTable
              allergies={getPagedData()}
              selectedAllergy={selectedAllergy}
              sortColumn={sortColumn}
              onSelect={handleAllergySelect}
              onSort={handleSort}
            />

            <div className="row justify-content-center">
              <Pagination
                itemsCount={totalCount}
                pageSize={pageSize}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          </>
        )}
        {selectedAllergy.UID && (
          <>
            {!selectedAllergy.ccx_client_allergyid && (
              <i
                className="fas fa-minus-circle"
                onClick={() => setSelectedAllergy({})}
              ></i>
            )}
            <label className="ms-2">
              Selected Allergy: {selectedAllergy.Description}
            </label>
            <Select
              name="allergyType"
              label="Allergy Type"
              options={allergyTypeOptions}
              value={selectedAllergy.allergyType}
              error={errors.allergyType}
              onChange={handleChange}
              disabled={selectedAllergy.ccx_client_allergyid != null}
            />
            <Select
              name="allergyReaction"
              label="Allergy Reaction"
              options={allergyReactionOptions}
              value={selectedAllergy.allergyReaction}
              error={errors.allergyReaction}
              onChange={handleChange}
              disabled={selectedAllergy.ccx_client_allergyid != null}
            />
            <Select
              name="ccx_severity"
              label="Allergy Severity"
              options={allergyOptionSets.ccx_severity}
              value={selectedAllergy.ccx_severity}
              error={errors.ccx_severity}
              onChange={handleChange}
              disabled={selectedAllergy.ccx_client_allergyid != null}
            />
            <div className="container">
              <div className="row">
                <div className="col">
                  <Input
                    name="ccx_effdate"
                    label="Effective Date"
                    type="date"
                    value={selectedAllergy.ccx_effdate}
                    error={errors.ccx_effdate}
                    onChange={handleChange}
                    disabled={
                      selectedAllergy.ccx_client_allergyid !== undefined
                    }
                  />
                </div>
                <div className="col">
                  {selectedAllergy.ccx_client_allergyid && (
                    <Input
                      name="ccx_expdate"
                      label="Expiration Date"
                      type="date"
                      value={
                        selectedAllergy.ccx_expdate
                          ? selectedAllergy.ccx_expdate
                          : ""
                      }
                      error={errors.ccx_expdate}
                      onChange={handleChange}
                    />
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {nextModal === undefined && (
          <>
            <button className="btn btn-danger" onClick={handleHideModal}>
              Cancel
            </button>
            <button
              className={
                Object.keys(errors).length === 0
                  ? "btn btn-primary"
                  : "btn btn-secondary"
              }
              disabled={Object.keys(errors).length !== 0}
              onClick={() => {
                if (!validate()) {
                  handleSave();
                  handleHideModal();
                }
              }}
            >
              Submit
            </button>
            <button
              className={
                Object.keys(errors).length === 0
                  ? "btn btn-primary"
                  : "btn btn-secondary"
              }
              disabled={Object.keys(errors).length !== 0}
              onClick={() => {
                if (!validate()) handleSaveAndAdd();
              }}
            >
              Submit and Add New
            </button>
          </>
        )}
        {nextModal && (
          <>
            <button
              className="btn btn-primary"
              onClick={() => {
                resetForm();
                nextModal();
              }}
            >
              Skip
            </button>
            <button
              className={
                Object.keys(errors).length === 0
                  ? "btn btn-primary"
                  : "btn btn-secondary"
              }
              disabled={Object.keys(errors).length !== 0}
              onClick={() => {
                if (!validate()) {
                  handleSave();
                  resetForm();
                  nextModal();
                }
              }}
            >
              Submit and {isLastModal ? "End" : "Next"}
            </button>
            <button
              className={
                Object.keys(errors).length === 0
                  ? "btn btn-primary"
                  : "btn btn-secondary"
              }
              disabled={Object.keys(errors).length !== 0}
              onClick={() => {
                if (!validate()) handleSaveAndAdd();
              }}
            >
              Submit and Add New
            </button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  ) : null;
}

export default AllergyModal;
